import React, { useState, useEffect } from 'react';

const CategoryManager = ({ categories, onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    parent_id: '',
    icon: '📦',
    color: '#00FFFF'
  });
  const [loading, setLoading] = useState(false);

  // 常用图标
  const commonIcons = ['📦', '💊', '🧴', '💄', '🧼', '🌸', '🌿', '⚡', '🔥', '❄️', '💧', '🌟', '🎯', '🎨', '🎭'];

  // 常用颜色
  const commonColors = [
    '#00FFFF', '#9D4EDD', '#FF006E', '#FB5607', '#FFBE0B',
    '#8338EC', '#3A86FF', '#06FFB4', '#FF4365', '#00D9FF'
  ];

  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name || '',
        parent_id: editingCategory.parent_id || '',
        icon: editingCategory.icon || '📦',
        color: editingCategory.color || '#00FFFF'
      });
    }
  }, [editingCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { db, auth } = await import('../supabaseClient');
      const user = await auth.getCurrentUser();

      if (!user) {
        throw new Error('用户未登录');
      }

      const categoryData = {
        ...formData,
        user_id: user.id,
        parent_id: formData.parent_id === '' ? null : formData.parent_id, // 修复：空字符串转 null
        level: formData.parent_id ? 2 : 1
      };

      let result;
      if (editingCategory) {
        // 更新分类
        result = await db.updateCategory(editingCategory.id, categoryData);
      } else {
        // 添加分类
        result = await db.addCategory(categoryData);
      }

      if (result.error) {
        throw new Error(result.error.message);
      }

      handleCloseForm();
      onRefresh();
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!confirm('确定要删除这个分类吗？如果该分类下有子分类或物品，它们也会被删除。')) {
      return;
    }

    try {
      const { db } = await import('../supabaseClient');
      const { error } = await db.deleteCategory(categoryId);
      
      if (error) {
        throw new Error(error.message);
      }
      
      onRefresh();
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败: ' + error.message);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowAddForm(true);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      parent_id: '',
      icon: '📦',
      color: '#00FFFF'
    });
  };

  // 构建分类树
  const buildCategoryTree = (categories) => {
    const tree = [];
    const categoryMap = {};
    
    categories.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });
    
    categories.forEach(cat => {
      if (cat.parent_id) {
        const parent = categoryMap[cat.parent_id];
        if (parent) {
          parent.children.push(categoryMap[cat.id]);
        }
      } else {
        tree.push(categoryMap[cat.id]);
      }
    });
    
    return tree;
  };

  // 渲染分类节点
  const renderCategoryNode = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    
    return (
      <div key={category.id} className="w-full">
        <div
          className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-lg p-4 hover:border-cyan-500/30 transition-all duration-200"
          style={{ marginLeft: `${level * 24}px` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                style={{
                  backgroundColor: category.color + '20',
                  border: `2px solid ${category.color}`,
                  boxShadow: `0 0 10px ${category.color}40`
                }}
              >
                {category.icon}
              </div>
              <div>
                <h3 className="text-lg font-medium text-cyan-400">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-400">
                  {category.level === 1 ? '一级分类' : '二级分类'}
                  {hasChildren && ` · ${category.children.length} 个子分类`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleEdit(category)}
                className="p-2 rounded bg-blue-900 text-blue-300 hover:bg-blue-800 transition-colors border border-blue-700"
                title="编辑"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              
              <button
                onClick={() => handleDelete(category.id)}
                className="p-2 rounded bg-red-900 text-red-300 hover:bg-red-800 transition-colors border border-red-700"
                title="删除"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {hasChildren && (
          <div className="mt-2 space-y-2">
            {category.children.map(child => renderCategoryNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const categoryTree = buildCategoryTree(categories);

  if (showAddForm) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-800/60 backdrop-blur-sm border border-cyan-500/20 rounded-lg p-6 shadow-2xl">
          <h2 className="text-2xl font-bold text-cyan-400 mb-6">
            {editingCategory ? '编辑分类' : '添加分类'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  分类名称 *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-600 text-cyan-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                  placeholder="如：护肤品"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  父分类
                </label>
                <select
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-600 text-cyan-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                >
                  <option value="">无（一级分类）</option>
                  {categories
                    .filter(cat => cat.level === 1 && (!editingCategory || cat.id !== editingCategory.id))
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>

            {/* 图标选择 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                图标
              </label>
              <div className="grid grid-cols-8 gap-2 mb-3">
                {commonIcons.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, icon }))}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.icon === icon
                        ? 'border-cyan-500 bg-cyan-500/20'
                        : 'border-slate-600 hover:border-cyan-500'
                    }`}
                  >
                    <span className="text-xl">{icon}</span>
                  </button>
                ))}
              </div>
              <input
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-600 text-cyan-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                placeholder="自定义图标"
              />
            </div>

            {/* 颜色选择 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                颜色
              </label>
              <div className="grid grid-cols-10 gap-2 mb-3">
                {commonColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    className={`w-full h-10 rounded-lg border-2 transition-all ${
                      formData.color === color
                        ? 'border-cyan-500'
                        : 'border-slate-600 hover:border-cyan-500'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-full h-10 rounded-lg bg-slate-900 border border-slate-600 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-600">
              <button
                type="button"
                onClick={handleCloseForm}
                className="px-6 py-3 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 transition-colors"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-900 font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-cyan-400 hover:to-blue-400 transition-all"
              >
                {loading ? '保存中...' : (editingCategory ? '更新分类' : '添加分类')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 头部操作栏 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-cyan-400">
            分类管理
          </h2>
          <p className="text-gray-400 mt-1">
            共 {categories.length} 个分类
          </p>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-900 font-bold rounded-lg shadow-2xl hover:from-cyan-400 hover:to-blue-400 transition-all"
        >
          ➕ 添加分类
        </button>
      </div>

      {/* 分类树 */}
      {categories.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏷️</div>
          <h3 className="text-xl font-medium text-gray-300 mb-2">
            暂无分类
          </h3>
          <p className="text-gray-500 mb-6">
            点击"添加分类"按钮开始创建你的分类体系
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-900 font-bold rounded-lg hover:from-cyan-400 hover:to-blue-400 transition-all"
          >
            添加第一个分类
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {categoryTree.map(category => renderCategoryNode(category))}
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
